import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import { TextField } from '../ui/TextField';
import { TextArea } from '../ui/TextArea';
import { FeatherX } from '../ui/icons';
import './LeaveApplicationForm.css';

const LeaveApplicationForm = ({ onSuccess, onCancel, year, balance }) => {
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    leave_type: '',
    reason: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [calculatedDays, setCalculatedDays] = useState(0);

  const totalLeaves = balance?.total_leaves || 18;
  const usedLeaves = balance?.used_leaves || 0;
  const remainingLeaves = Math.max(0, balance?.remaining_leaves ?? (totalLeaves - usedLeaves));
  const quotaDays = Math.min(calculatedDays, remainingLeaves);
  const lopDays = Math.max(0, calculatedDays - remainingLeaves);

  useEffect(() => {
    if (formData.start_date && formData.end_date) {
      if (formData.end_date >= formData.start_date) {
        setCalculatedDays(calculateBusinessDays(formData.start_date, formData.end_date));
      } else {
        setCalculatedDays(0);
      }
    } else {
      setCalculatedDays(0);
    }
  }, [formData.start_date, formData.end_date]);

  const calculateBusinessDays = (startDate, endDate) => {
    const start = new Date(`${String(startDate).split('T')[0]}T00:00:00.000Z`);
    const end = new Date(`${String(endDate).split('T')[0]}T00:00:00.000Z`);
    let count = 0;
    const current = new Date(start);

    while (current <= end) {
      const dayOfWeek = current.getUTCDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        count++;
      }
      current.setUTCDate(current.getUTCDate() + 1);
    }

    return count;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const submitData = {
        start_date: formData.start_date,
        end_date: formData.end_date,
        leave_type: formData.leave_type || null,
        reason: formData.reason || null
      };

      await onSuccess(submitData);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit leave application');
      console.error('Error submitting leave application:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return createPortal(
    <div className="leave-application-form-overlay" onClick={onCancel}>
      <div className="leave-application-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="leave-application-form-header">
          <h2>Apply for Leave</h2>
          <button className="leave-application-form-close" onClick={onCancel}>
            <FeatherX />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="leave-application-form-content">
          {error && (
            <div className="leave-application-form-error">
              {error}
            </div>
          )}

          <div className="leave-application-form-row">
            <TextField label="Start Date *">
              <TextField.Input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleChange('start_date', e.target.value)}
                required
                min={new Date().toISOString().split('T')[0]}
              />
            </TextField>

            <TextField label="End Date *">
              <TextField.Input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleChange('end_date', e.target.value)}
                required
                min={formData.start_date || new Date().toISOString().split('T')[0]}
              />
            </TextField>
          </div>

          {calculatedDays > 0 && (
            <div className={`leave-application-form-days-info${lopDays > 0 ? ' has-lop' : ''}`}>
              <span>Total Leave Days: <strong>{calculatedDays}</strong></span>
              <span className="leave-application-form-remaining">
                Remaining quota: <strong>{remainingLeaves}</strong>
              </span>
              {lopDays > 0 ? (
                <span className="leave-application-form-lop-warning">
                  {quotaDays} day{quotaDays === 1 ? '' : 's'} from quota, {lopDays} day{lopDays === 1 ? '' : 's'} will be LOP after approval
                </span>
              ) : (
                <span>All {calculatedDays} day{calculatedDays === 1 ? '' : 's'} will be counted against your yearly quota.</span>
              )}
            </div>
          )}

          <TextField label="Leave Type">
            <select
              className="leave-application-form-select"
              value={formData.leave_type}
              onChange={(e) => handleChange('leave_type', e.target.value)}
            >
              <option value="">Select leave type (optional)</option>
              <option value="vacation">Vacation</option>
              <option value="sick">Sick Leave</option>
              <option value="personal">Personal</option>
              <option value="emergency">Emergency</option>
              <option value="other">Other</option>
            </select>
          </TextField>

          <TextArea label="Reason">
            <TextArea.Input
              value={formData.reason}
              onChange={(e) => handleChange('reason', e.target.value)}
              placeholder="Optional reason for leave"
              rows="3"
            />
          </TextArea>

          <div className="leave-application-form-actions">
            <Button
              type="button"
              variant="neutral-tertiary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading || !formData.start_date || !formData.end_date || calculatedDays === 0}
            >
              {loading ? 'Submitting...' : 'Apply for Leave'}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default LeaveApplicationForm;

